/**
 * Notification preference contract — ONE source of truth.
 *
 * Why this file exists
 * --------------------
 * `public.notification_preferences` is a SPARSE table: a row is written only when
 * a user changes something, so "no row" is the normal state, not an error state.
 * (Platform-wide it currently holds zero rows.) Every surface therefore has to
 * agree on what a missing row MEANS, and before this module they did not:
 *
 *   - the category rows in the settings page defaulted each toggle to `?? true`
 *   - the channel master switches asked `preferences.some(p => p.emailEnabled)`,
 *     which on an empty array is `false`
 *
 * …so the page rendered both channel masters OFF above ten categories all ON, an
 * arrangement no stored state can produce. The masters must be derived from the
 * SAME effective preferences the rows render, which is what `DEFAULT_PREFERENCE`
 * plus `withDefaults()` below guarantee.
 *
 * The defaults are the column DEFAULTs of public.notification_preferences —
 * enabled/inapp_enabled/push_enabled/sound_enabled true, email_enabled false,
 * digest_mode 'realtime', min_priority 'low' — so a user who has never touched
 * the page sees exactly what the database would give them on first write.
 */

import type {
  DigestMode,
  NotificationCategory,
  NotificationPreference,
  NotificationPriority,
} from '@/store/slices/notificationsTypes';

/**
 * Categories that public.notification_preferences.category accepts.
 *
 * Mirrors notification_preferences_category_check. '_all' is the account-wide
 * default row, not a UI category — it is valid to store but never rendered as a
 * row in the category list.
 */
export const PREFERENCE_CATEGORIES: NotificationCategory[] = [
  'system',
  'call',
  'report',
  'action_required',
  'comment',
  'mention',
  'alert',
  'ai_suggestion',
  'ai_analysis_complete',
  'achievement',
];

export const ALL_CATEGORIES_KEY = '_all';

export const DIGEST_MODES: DigestMode[] = ['realtime', 'hourly', 'daily', 'weekly'];
export const MIN_PRIORITIES: NotificationPriority[] = ['low', 'normal', 'high', 'urgent'];

export function isStorableCategory(value: unknown): value is NotificationCategory | '_all' {
  return (
    typeof value === 'string' &&
    (value === ALL_CATEGORIES_KEY || (PREFERENCE_CATEGORIES as string[]).includes(value))
  );
}

/** Effective preference for a category with no stored row. */
export type EffectiveNotificationPreference = Omit<NotificationPreference, 'id' | 'userId'> & {
  /** True when this is a default, i.e. no row exists in the database yet. */
  isDefault: boolean;
};

export const DEFAULT_PREFERENCE: Omit<EffectiveNotificationPreference, 'category'> = {
  enabled: true,
  inappEnabled: true,
  pushEnabled: true,
  emailEnabled: false,
  digestMode: 'realtime',
  quietHoursEnabled: false,
  quietHoursStart: undefined,
  quietHoursEnd: undefined,
  soundEnabled: true,
  soundId: 'default',
  minPriority: 'low',
  isDefault: true,
};

/**
 * Snake_case shape the API returns and the hook consumes. Mirrors the
 * notification_preferences columns (the table has no org_unit_id).
 */
export interface NotificationPreferenceRow {
  id?: string | null;
  user_id?: string | null;
  category: string;
  enabled: boolean;
  inapp_enabled: boolean;
  push_enabled: boolean;
  email_enabled: boolean;
  digest_mode: string;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  sound_enabled: boolean;
  sound_id: string;
  min_priority: string;
  /** True when the server synthesised this from defaults (no stored row). */
  is_default?: boolean;
}

/** The default row for a category, in the API's snake_case shape. */
export function defaultPreferenceRow(category: string): NotificationPreferenceRow {
  return {
    id: null,
    category,
    enabled: DEFAULT_PREFERENCE.enabled,
    inapp_enabled: DEFAULT_PREFERENCE.inappEnabled,
    push_enabled: DEFAULT_PREFERENCE.pushEnabled,
    email_enabled: DEFAULT_PREFERENCE.emailEnabled,
    digest_mode: DEFAULT_PREFERENCE.digestMode,
    quiet_hours_enabled: DEFAULT_PREFERENCE.quietHoursEnabled,
    quiet_hours_start: null,
    quiet_hours_end: null,
    sound_enabled: DEFAULT_PREFERENCE.soundEnabled,
    sound_id: DEFAULT_PREFERENCE.soundId,
    min_priority: DEFAULT_PREFERENCE.minPriority,
    is_default: true,
  };
}

/**
 * Effective preference for a category: the stored row if there is one, otherwise
 * the default. NEVER returns undefined — that is the whole point. Any surface
 * that renders a toggle must go through this so the masters and the rows can
 * never disagree.
 */
export function effectivePreference(
  category: NotificationCategory,
  stored: NotificationPreference[] | undefined | null,
): EffectiveNotificationPreference {
  const row = (stored || []).find((p) => p.category === category);
  if (!row) {
    return { ...DEFAULT_PREFERENCE, category };
  }
  return {
    category,
    enabled: row.enabled ?? DEFAULT_PREFERENCE.enabled,
    inappEnabled: row.inappEnabled ?? DEFAULT_PREFERENCE.inappEnabled,
    pushEnabled: row.pushEnabled ?? DEFAULT_PREFERENCE.pushEnabled,
    emailEnabled: row.emailEnabled ?? DEFAULT_PREFERENCE.emailEnabled,
    digestMode: row.digestMode ?? DEFAULT_PREFERENCE.digestMode,
    quietHoursEnabled: row.quietHoursEnabled ?? DEFAULT_PREFERENCE.quietHoursEnabled,
    quietHoursStart: row.quietHoursStart,
    quietHoursEnd: row.quietHoursEnd,
    soundEnabled: row.soundEnabled ?? DEFAULT_PREFERENCE.soundEnabled,
    soundId: row.soundId ?? DEFAULT_PREFERENCE.soundId,
    minPriority: row.minPriority ?? DEFAULT_PREFERENCE.minPriority,
    isDefault: false,
  };
}

/** Effective preferences for every renderable category, in display order. */
export function effectivePreferences(
  stored: NotificationPreference[] | undefined | null,
): EffectiveNotificationPreference[] {
  return PREFERENCE_CATEGORIES.map((category) => effectivePreference(category, stored));
}

/**
 * Is a delivery channel on?
 *
 * "On" means at least one ENABLED category delivers through it — evaluated over
 * EFFECTIVE preferences, so with zero stored rows this reports the defaults
 * (in-app on, push on, email off) instead of a flat false for everything.
 */
export function isChannelEnabled(
  channel: 'inapp' | 'push' | 'email',
  stored: NotificationPreference[] | undefined | null,
): boolean {
  const key =
    channel === 'inapp' ? 'inappEnabled' : channel === 'push' ? 'pushEnabled' : 'emailEnabled';
  return effectivePreferences(stored).some((p) => p.enabled && p[key]);
}

/**
 * May a notification of this category be shown in-app (toast or bell)? The ONE delivery predicate: `notify()` and the
 * recording-notice toast both ask it. An ABSENT preference means allow (a user who never opened the settings page has no
 * rows); only an explicit opt-out of the category, or of in-app delivery for it, suppresses.
 */
export function inAppDeliveryAllowed(
  category: NotificationCategory,
  stored: NotificationPreference[] | undefined | null,
): boolean {
  const effective = effectivePreference(category, stored);
  return effective.enabled !== false && effective.inappEnabled !== false;
}
